import { FetchError, SWRFetcher } from '@commenda/utils';
import useSWR, { KeyedMutator } from 'swr';
import {
  IIndirectTaxListRegistrationsV2Response,
  IUseIndirectTaxRegistrationsParams,
} from './types';

// Corporations without a sales-tax organisation 404 on this endpoint;
// treat that as "no registrations" instead of surfacing a fetch error.
const listFetcher = async (url: string) => {
  try {
    return await SWRFetcher({ method: 'GET' })(url);
  } catch (err) {
    if (err instanceof FetchError && err.status === 404) {
      return { registrations: [], count: 0 };
    }
    throw err;
  }
};

export function useIndirectTaxRegistrations(
  params: IUseIndirectTaxRegistrationsParams | null
) {
  const filters = params?.filters ?? {};
  const query = new URLSearchParams();

  if (filters.corporationId) {
    query.append('corporationId', filters.corporationId.toString());
  }
  if (filters.countries?.length) {
    filters.countries.forEach((c) => query.append('countries', c));
  }
  if (filters.states?.length) {
    filters.states.forEach((s) => query.append('states', s));
  }
  if (filters.jurisdictionIds?.length) {
    filters.jurisdictionIds.forEach((id) =>
      query.append('jurisdictionIds', id)
    );
  }
  if (filters.jurisdictionTypes?.length) {
    filters.jurisdictionTypes.forEach((t) =>
      query.append('jurisdictionTypes', t)
    );
  }
  if (filters.jurisdictionName) {
    query.append('jurisdictionName', filters.jurisdictionName);
  }
  if (filters.registrationStatus) {
    query.append('registrationStatus', filters.registrationStatus);
  }
  if (filters.validationStatus) {
    query.append('validationStatus', filters.validationStatus);
  }
  if (filters.includeArchived !== undefined) {
    query.append('includeArchived', filters.includeArchived.toString());
  }
  if (filters.cursor) {
    query.append('cursor', filters.cursor);
  }
  if (filters.limit) {
    query.append('limit', filters.limit.toString());
  }

  const {
    data,
    mutate,
    error,
    isLoading,
  }: {
    data?: IIndirectTaxListRegistrationsV2Response;
    error?: unknown;
    mutate?: KeyedMutator<unknown>;
    isLoading: boolean;
  } = useSWR(
    // The backend requires corporationId; don't fire until it's resolved
    // (callers pass undefined/NaN during initial render).
    filters.corporationId
      ? `new-sales-tax/registrations-v2/list?${query}`
      : null,
    listFetcher,
    { keepPreviousData: false }
  );

  return {
    data: data?.registrations,
    total: data?.total ?? 0,
    cursor: data?.cursor,
    mutate,
    error,
    isLoading,
  };
}
